/**
 * Eltern-Verweise im Dokumentkopf (Regel „Dokument-Navigation" R1: „Ich muss immer im
 * Kopf eines untergeordneten Dokumentes die Eltern verlinkt haben, also bei einem Bericht
 * die Aufgabe, bei einer Aufgabe den Kunden").
 *
 * Kunden-Verweise zeigen ausschließlich auf den Kontakt im Adressbuch. Es gibt zwei
 * ID-Räume (Contact führend, customers.id ≠ contact_id) — aus einer Kunden-ID darf nie
 * ein Link gebaut werden.
 */
export type ParentKind =
  | 'kunde' | 'firma' | 'person' | 'projekt' | 'aufgabe' | 'bericht' | 'dokument' | 'ordner'
  | 'vorgang' | 'rechnung' | 'angebot' | 'fahrzeug' | 'geraet' | 'wissensbasis' | 'instanz';

export interface ParentRef {
  kind: ParentKind;
  label: string;
  /** Ziel; fehlt es, erscheint der Name als Text */
  to?: string | null;
  /** Link nur, wenn der Anwender dieses Modul öffnen darf */
  requiresModule?: string;
}

export const PARENT_KIND_LABEL: Record<ParentKind, string> = {
  kunde: 'Kunde', firma: 'Firma', person: 'Mitarbeiter', projekt: 'Projekt', aufgabe: 'Aufgabe', bericht: 'Bericht',
  dokument: 'Dokument', ordner: 'Ordner', vorgang: 'Vorgang', rechnung: 'Rechnung', angebot: 'Angebot',
  fahrzeug: 'Fahrzeug', geraet: 'Gerät', wissensbasis: 'Wissensbasis', instanz: 'Instanz',
};

function clean(s: string | null | undefined): string {
  return (s ?? '').trim();
}
function uuid(s: string | null | undefined): string | null {
  const v = clean(s);
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) ? v : null;
}
function ref(kind: ParentKind, name: string | null | undefined, to: string | null, requiresModule?: string): ParentRef | null {
  const label = clean(name);
  if (!label) return null;
  return { kind, label, to, ...(requiresModule ? { requiresModule } : {}) };
}

export const parentRef = {
  /** contactId = addressbook.contacts.id — NIE stammdaten.customers.id */
  kunde(contactId: string | null | undefined, name: string | null | undefined): ParentRef | null {
    const id = uuid(contactId);
    return ref('kunde', name, id ? `/admin/adressbuch/contacts/${id}` : null, 'adressbuch');
  },
  firma(contactId: string | null | undefined, name: string | null | undefined): ParentRef | null {
    const id = uuid(contactId);
    return ref('firma', name, id ? `/admin/adressbuch/contacts/${id}` : null, 'adressbuch');
  },
  personByUser(userId: string | null | undefined, name: string | null | undefined): ParentRef | null {
    const id = uuid(userId);
    return ref('person', name, id ? `/admin/adressbuch/persons/by-user/${id}` : null, 'adressbuch');
  },
  aufgabe(id: string | null | undefined, title: string | null | undefined): ParentRef | null {
    const v = uuid(id);
    return ref('aufgabe', title, v ? `/m/bauberichteV2/tasks/${v}` : null, 'bauberichteV2');
  },
  bericht(id: string | null | undefined, title: string | null | undefined): ParentRef | null {
    const v = uuid(id);
    return ref('bericht', title, v ? `/m/bauberichteV2/reports/${v}` : null, 'bauberichteV2');
  },
  projekt(id: string | null | undefined, name: string | null | undefined): ParentRef | null {
    const v = uuid(id);
    return ref('projekt', name, v ? `/m/bauberichteV2/projekte/${v}` : null, 'bauberichteV2');
  },
};

/** Ziel des Verweises, wenn der Anwender es öffnen darf; sonst null (→ Text). */
export function linkTarget(p: ParentRef, canOpenModule: (slug: string) => boolean): string | null {
  if (!p.to) return null;
  if (p.requiresModule && !canOpenModule(p.requiresModule)) return null;
  return p.to;
}
